import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  acceptSuggestion,
  bulkUpdateTasks,
  createTask,
  deleteTask,
  getOrganizerCalendar,
  getTaskCounts,
  listFathomIngests,
  listSuggestions,
  listTasks,
  moveTask,
  rejectSuggestion,
  reorderColumn,
  syncFathom,
  getFathomSyncStatus,
  updateTask,
  type CreateTaskInput,
  type MoveTaskInput,
  type Task,
  type TaskPatch,
  type TaskStatus,
  type TaskSuggestion,
} from '../api/organizer';
import { rankBetween } from '@sequesto-os/shared';

const TASKS_KEY = ['organizer', 'tasks'] as const;
const COUNTS_KEY = ['organizer', 'counts'] as const;
const SUGGESTIONS_KEY = ['organizer', 'suggestions'] as const;
const CALENDAR_KEY = ['organizer', 'calendar'] as const;

interface TasksResponse {
  tasks: Task[];
  statuses?: TaskStatus[];
}

interface SuggestionsResponse {
  suggestions: TaskSuggestion[];
}

/** Marker for a row that exists only optimistically and has no server id yet. */
export const TEMP_ID_PREFIX = 'temp-';

export function isTempTask(task: Task): boolean {
  return task.id.startsWith(TEMP_ID_PREFIX);
}

/**
 * Snapshot the task cache, apply an optimistic change, and hand back the
 * rollback context.
 *
 * `cancelQueries` first: an in-flight refetch that lands after the optimistic
 * write would overwrite it with pre-change data, which reads as the card
 * silently jumping back.
 */
async function optimisticTasks(
  qc: QueryClient,
  apply: (tasks: Task[]) => Task[],
): Promise<{ previous: TasksResponse | undefined }> {
  await qc.cancelQueries({ queryKey: TASKS_KEY });
  const previous = qc.getQueryData<TasksResponse>(TASKS_KEY);
  qc.setQueryData<TasksResponse>(TASKS_KEY, (old) =>
    old ? { ...old, tasks: apply(old.tasks) } : old,
  );
  return { previous };
}

function replaceTask(qc: QueryClient, task: Task): void {
  qc.setQueryData<TasksResponse>(TASKS_KEY, (old) =>
    old ? { ...old, tasks: old.tasks.map((t) => (t.id === task.id ? task : t)) } : old,
  );
}

function mergeColumn(qc: QueryClient, column: Task[]): void {
  const byId = new Map(column.map((t) => [t.id, t]));
  qc.setQueryData<TasksResponse>(TASKS_KEY, (old) =>
    old ? { ...old, tasks: old.tasks.map((t) => byId.get(t.id) ?? t) } : old,
  );
}

// ── Queries ─────────────────────────────────────────────────────────────────

/**
 * The whole board in one cache entry, unparameterised by status or filter.
 *
 * Columns and filters are derived client-side. Spreading the board across
 * per-status query keys would mean touching several caches on every drag, which
 * is exactly where optimistic reordering tends to break.
 */
export function useBoardTasks() {
  return useQuery({
    queryKey: TASKS_KEY,
    queryFn: () => listTasks(false),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Overdue and due-today counts.
 *
 * Mounted by the Sidebar, which renders once for every private route, so this is
 * the only poller in the app. The Organizer page calls the same hook and gets
 * the shared cache entry, which is what keeps the badge and the page in step.
 */
export function useTaskCounts(enabled = true) {
  return useQuery({
    queryKey: COUNTS_KEY,
    queryFn: getTaskCounts,
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useSuggestions(enabled = true) {
  return useQuery({
    queryKey: SUGGESTIONS_KEY,
    queryFn: () => listSuggestions('pending'),
    enabled,
    staleTime: 60_000,
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

/**
 * Patch a task — inline title edits, due date, priority, and the done checkbox.
 *
 * `onSettled` invalidates counts but deliberately NOT the task list: with rapid
 * interactions a blanket invalidation races the next optimistic write and makes
 * cards visibly jump. The server row is merged in `onSuccess` instead.
 */
export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskPatch }) => updateTask(id, patch),
    onMutate: ({ id, patch }) =>
      optimisticTasks(qc, (tasks) => tasks.map((t) => (t.id === id ? { ...t, ...patch } : t))),
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(TASKS_KEY, ctx.previous);
    },
    onSuccess: ({ task }) => replaceTask(qc, task),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      // The calendar is a separate, server-computed cache (it owns the Brussels
      // day maths), so it is refreshed rather than patched optimistically.
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => createTask(input),
    onMutate: async (input) => {
      const tempId = `${TEMP_ID_PREFIX}${crypto.randomUUID()}`;
      const now = new Date().toISOString();
      const ctx = await optimisticTasks(qc, (tasks) => [
        {
          id: tempId,
          title: input.title,
          notes: input.notes ?? null,
          status: input.status ?? 'todo',
          priority: input.priority ?? 'medium',
          dueDate: input.dueDate ?? null,
          remindAt: null,
          labels: input.labels ?? [],
          source: 'manual',
          sourceRef: null,
          sourceMeta: null,
          suggestionId: null,
          // Sorts to the top of its column, matching the server's default.
          rank: '0',
          completedAt: null,
          createdAt: now,
          updatedAt: now,
        },
        ...tasks,
      ]);
      return { ...ctx, tempId };
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(TASKS_KEY, ctx.previous);
    },
    // Swap the placeholder for the real row rather than appending, or the task
    // appears twice until the next refetch.
    onSuccess: ({ task }, _vars, ctx) => {
      qc.setQueryData<TasksResponse>(TASKS_KEY, (old) =>
        old
          ? { ...old, tasks: old.tasks.map((t) => (t.id === ctx?.tempId ? task : t)) }
          : old,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      // The calendar is a separate, server-computed cache (it owns the Brussels
      // day maths), so it is refreshed rather than patched optimistically.
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTask(id),
    onMutate: (id) => optimisticTasks(qc, (tasks) => tasks.filter((t) => t.id !== id)),
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(TASKS_KEY, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      // The calendar is a separate, server-computed cache (it owns the Brussels
      // day maths), so it is refreshed rather than patched optimistically.
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

/**
 * Drag-and-drop: one request per completed drag, not per hover.
 *
 * The optimistic rank is computed the same way the server will compute it, so
 * the card lands where it was dropped instead of snapping to a column edge and
 * back. `onSuccess` then takes the server's version of the column, which is
 * authoritative — and necessary when the server rebalanced the ranks.
 */
export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MoveTaskInput) => moveTask(input),
    onMutate: ({ taskId, status, beforeId, afterId }) =>
      optimisticTasks(qc, (tasks) => {
        const before = beforeId ? tasks.find((t) => t.id === beforeId) : undefined;
        const after = afterId ? tasks.find((t) => t.id === afterId) : undefined;
        let rank: string;
        try {
          rank = rankBetween(before?.rank ?? null, after?.rank ?? null);
        } catch {
          // A stale view of the column. The server recomputes and returns the
          // real order, so an approximate local rank is fine for one frame.
          rank = before?.rank ?? after?.rank ?? '0';
        }
        return tasks.map((t) => (t.id === taskId ? { ...t, status, rank } : t));
      }),
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(TASKS_KEY, ctx.previous);
    },
    onSuccess: ({ task, column }) => {
      mergeColumn(qc, column);
      replaceTask(qc, task);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      // The calendar is a separate, server-computed cache (it owns the Brussels
      // day maths), so it is refreshed rather than patched optimistically.
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

export function useReorderColumn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ status, orderedIds }: { status: TaskStatus; orderedIds: string[] }) =>
      reorderColumn(status, orderedIds),
    onSuccess: ({ column }) => mergeColumn(qc, column),
  });
}

export function useBulkUpdateTasks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      action,
    }: {
      ids: string[];
      action: 'complete' | 'archive' | 'delete' | 'reopen';
    }) => bulkUpdateTasks(ids, action),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: TASKS_KEY });
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

// ── Review inbox ────────────────────────────────────────────────────────────

/**
 * Accept a suggestion.
 *
 * Only the suggestion list is updated optimistically. The task itself is added
 * from the server response, never optimistically: "nothing enters the real list
 * unreviewed" also means a failed accept must not leave a phantom on the board.
 */
export function useAcceptSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, edits }: { id: string; edits?: TaskPatch }) =>
      acceptSuggestion(id, edits ?? {}),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: SUGGESTIONS_KEY });
      const previous = qc.getQueryData<SuggestionsResponse>(SUGGESTIONS_KEY);
      qc.setQueryData<SuggestionsResponse>(SUGGESTIONS_KEY, (old) =>
        old ? { suggestions: old.suggestions.filter((s) => s.id !== id) } : old,
      );
      return { previous };
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(SUGGESTIONS_KEY, ctx.previous);
    },
    onSuccess: ({ task }) => {
      qc.setQueryData<TasksResponse>(TASKS_KEY, (old) =>
        old ? { ...old, tasks: [task, ...old.tasks] } : old,
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      // The calendar is a separate, server-computed cache (it owns the Brussels
      // day maths), so it is refreshed rather than patched optimistically.
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

export function useRejectSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => rejectSuggestion(id, reason),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: SUGGESTIONS_KEY });
      const previous = qc.getQueryData<SuggestionsResponse>(SUGGESTIONS_KEY);
      qc.setQueryData<SuggestionsResponse>(SUGGESTIONS_KEY, (old) =>
        old ? { suggestions: old.suggestions.filter((s) => s.id !== id) } : old,
      );
      return { previous };
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(SUGGESTIONS_KEY, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      // The calendar is a separate, server-computed cache (it owns the Brussels
      // day maths), so it is refreshed rather than patched optimistically.
      void qc.invalidateQueries({ queryKey: CALENDAR_KEY });
    },
  });
}

const INGESTS_KEY = ['organizer', 'fathomIngests'] as const;

/**
 * Recent Fathom ingest attempts.
 *
 * The point of surfacing these is that "no suggestions" has two very different
 * causes — nothing was committed on the calls, or the pipeline is broken — and
 * only this list tells them apart.
 */
export function useFathomIngests(enabled = true) {
  return useQuery({
    queryKey: INGESTS_KEY,
    queryFn: () => listFathomIngests(25),
    enabled,
    staleTime: 60_000,
  });
}

export function useSyncFathom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lookbackHours?: number) => syncFathom(lookbackHours),
    // The job runs in the background, so refresh both the inbox and the audit
    // list shortly after it is queued rather than pretending it finished.
    onSuccess: () => {
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: SUGGESTIONS_KEY });
        void qc.invalidateQueries({ queryKey: INGESTS_KEY });
        void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      }, 4000);
    },
  });
}

/**
 * Follow a queued Fathom check to its end.
 *
 * Queuing succeeding is not the same as the check finding anything, and the
 * interesting failures — a key that sees no calls, a `recordedByEmail` matching
 * no recorder — only exist once the job has run. Polling stops as soon as the
 * job reaches a terminal state, so this is a short burst, not a standing poll.
 */
export function useFathomSyncStatus(jobId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['organizer', 'fathom-sync', jobId],
    queryFn: async () => {
      const status = await getFathomSyncStatus(jobId!);
      // The check is done, so whatever it produced is now worth showing. The
      // blind 4s refresh after queuing cannot cover a run that takes a minute.
      if (status.status === 'ready') {
        void qc.invalidateQueries({ queryKey: SUGGESTIONS_KEY });
        void qc.invalidateQueries({ queryKey: INGESTS_KEY });
        void qc.invalidateQueries({ queryKey: COUNTS_KEY });
      }
      return status;
    },
    enabled: !!jobId,
    retry: 2,
    refetchInterval: (query) => {
      // A failed request leaves `data` undefined forever, so liveness cannot be
      // inferred from it — without this the poll runs for as long as the page
      // is open and the panel never stops saying it is still checking.
      if (query.state.status === 'error') return false;
      const status = query.state.data?.status;
      return status === undefined || status === 'running' ? 2000 : false;
    },
    // A check is a point-in-time event; a cached verdict from a previous one
    // would be shown against the run the user just started.
    gcTime: 0,
    staleTime: 0,
  });
}

/**
 * The merged calendar for a window of days.
 *
 * `placeholderData` keeps the previous month on screen while the next one loads,
 * so paging does not blank the grid — the same instinct as the board's
 * `isFetching && !isLoading` guard.
 */
export function useOrganizerCalendar(from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: [...CALENDAR_KEY, from, to],
    queryFn: () => getOrganizerCalendar(from, to),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    placeholderData: keepPreviousData,
  });
}
